// =========================================
// Criteria Types - 5 evaluation criterion types
// =========================================

// Base criterion interface
export interface Criterion {
  id: string;
  user_id: string;
  name: string;
  description: string | null;
  type: CriterionType;
  config: CriterionConfig;
  weight: number; // 1-5
  is_template: boolean;
  category: string | null;
  created_at: string;
  updated_at: string;
}

export type CriterionType = 'boolean' | 'scale' | 'text_match' | 'pico' | 'bias_risk';

// =========================================
// Config types per criterion type
// =========================================

export interface BooleanConfig {
  question: string;
}

export interface ScaleConfig {
  min: number;
  max: number;
  step: number;
  labels: Record<string, string>; // e.g. { "1": "Very poor", "5": "Excellent" }
}

export interface TextMatchConfig {
  keywords: string[];
  mode: 'any' | 'all';
  case_sensitive: boolean;
}

export interface PICOConfig {
  population: string;
  intervention: string;
  comparator: string;
  outcome: string;
  instructions: string;
}

export interface BiasRiskConfig {
  checklist: string[];
  scale: string[]; // e.g. ["low", "some_concerns", "high"]
}

export type CriterionConfig =
  | BooleanConfig
  | ScaleConfig
  | TextMatchConfig
  | PICOConfig
  | BiasRiskConfig;

// =========================================
// Form/create types
// =========================================

export interface CreateCriterionInput {
  name: string;
  description?: string;
  type: CriterionType;
  config: CriterionConfig;
  weight: number;
  category?: string;
}

export interface UpdateCriterionInput {
  name?: string;
  description?: string;
  config?: CriterionConfig;
  weight?: number;
  category?: string;
}

// =========================================
// Template categories
// =========================================

export type TemplateCategory =
  | 'Quality'
  | 'Methodology'
  | 'Relevance'
  | 'Framework'
  | 'Bias'
  | 'Study Design'
  | 'JBI'
  | 'CASP';

// =========================================
// Type guards
// =========================================

export function isBooleanConfig(config: CriterionConfig): config is BooleanConfig {
  return 'question' in config;
}

export function isScaleConfig(config: CriterionConfig): config is ScaleConfig {
  return 'min' in config && 'max' in config && 'step' in config;
}

export function isTextMatchConfig(config: CriterionConfig): config is TextMatchConfig {
  return 'keywords' in config && 'mode' in config;
}

export function isPICOConfig(config: CriterionConfig): config is PICOConfig {
  return 'population' in config && 'intervention' in config;
}

export function isBiasRiskConfig(config: CriterionConfig): config is BiasRiskConfig {
  return 'checklist' in config && 'scale' in config;
}

// =========================================
// Default configs per type
// =========================================

export const DEFAULT_CONFIGS: Record<CriterionType, CriterionConfig> = {
  boolean: {
    question: '',
  } as BooleanConfig,
  scale: {
    min: 1,
    max: 5,
    step: 1,
    labels: { '1': 'Low', '3': 'Medium', '5': 'High' },
  } as ScaleConfig,
  text_match: {
    keywords: [],
    mode: 'any',
    case_sensitive: false,
  } as TextMatchConfig,
  pico: {
    population: '',
    intervention: '',
    comparator: '',
    outcome: '',
    instructions: '',
  } as PICOConfig,
  bias_risk: {
    checklist: [],
    scale: ['low', 'some_concerns', 'high'],
  } as BiasRiskConfig,
};

// =========================================
// Display helpers
// =========================================

export const CRITERION_TYPE_LABELS: Record<CriterionType, string> = {
  boolean: 'Yes/No',
  scale: 'Scale',
  text_match: 'Text Match',
  pico: 'PICO',
  bias_risk: 'Bias Risk',
};

export const CRITERION_TYPE_DESCRIPTIONS: Record<CriterionType, string> = {
  boolean: 'Simple yes or no question about the source',
  scale: 'Numeric rating on a configurable scale',
  text_match: 'Check for presence of specific keywords',
  pico: 'Population, Intervention, Comparator, Outcome framework',
  bias_risk: 'Evaluate risk of bias using a standardized checklist',
};
